/*    
<summary>
  This functional component "InactivityPopup" provides popup 
  Developer:Aashish Singh, Created Date:10-July-2024
</summary>
<returns>Returns JSX</returns>
*/
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { observer } from "mobx-react";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useStore } from "../../../contexts/store-provider";
import RoutesConstants from "../../routes/routes-constants";
import { formatMessage } from "../../../translations/format-message";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { getUserTypeFromLocalStorage } from "../../../helpers/localstorage-helper";

const INACTIVITY_LIMIT = 60*60 * 1000; // 1 min (change as needed)
const AUTO_LOGOUT_LIMIT = 20 * 1000; // 20 sec after popup
const STORAGE_KEY = "lizera_last_activity";

const InactivityPopup: React.FC = () => {
  const { authStore } = useStore();
  const { logout, logoutSuccess, logoutError, resetLogout } = authStore;
  const history = useHistory();

  const [showModal, setShowModal] = useState(false);

  const idleTimerRef = useRef<number | null>(null);
  const autoLogoutRef = useRef<number | null>(null);
  const isLoggingOutRef = useRef(false);

  const writeLastActivity = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  const getElapsedTime = () => {
    const last = Number(localStorage.getItem(STORAGE_KEY));
    if (!last) return 0;
    return Date.now() - last;
  };

  const clearAllTimers = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (autoLogoutRef.current) {
      clearTimeout(autoLogoutRef.current);
      autoLogoutRef.current = null;
    }
  };

  const triggerLogout = () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    clearAllTimers();
    setShowModal(false);
    logout();
  };

  const scheduleIdleCheck = () => {
    clearAllTimers();

    const remaining = INACTIVITY_LIMIT - getElapsedTime();

    if (remaining <= 0) {
      triggerLogout();
      return;
    }

    idleTimerRef.current = window.setTimeout(() => {
      if (getElapsedTime() >= INACTIVITY_LIMIT) {
        setShowModal(true);
      }
    }, remaining);
  };


  const handleActivity = () => {
    if (showModal || isLoggingOutRef.current) return;
    writeLastActivity();
    scheduleIdleCheck();
  };

  const handleResume = () => {
    if (isLoggingOutRef.current) return;

    const elapsed = getElapsedTime();

    if (elapsed >= INACTIVITY_LIMIT) {
      triggerLogout();
    } else {
      scheduleIdleCheck();
    }
  };

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      writeLastActivity();
    }

    if (getElapsedTime() >= INACTIVITY_LIMIT) {
      triggerLogout();
      return;
    }

    scheduleIdleCheck();

    const activityEvents = [
      "mousedown",
      "mousemove",
      "touchstart",
      "keydown",
      "scroll",
      "wheel",
    ];

    activityEvents.forEach(event =>
      document.addEventListener(event, handleActivity, { passive: true })
    );

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        handleResume();
      }
    });

    window.addEventListener("focus", handleResume);

    return () => {
      clearAllTimers();
      activityEvents.forEach(event =>
        document.removeEventListener(event, handleActivity)
      );
      window.removeEventListener("focus", handleResume);
    };
  }, []);

  // -------------------------------------------
  // Auto logout after popup
  // -------------------------------------------
  useEffect(() => {
    if (!showModal) return;

    autoLogoutRef.current = window.setTimeout(() => {
      triggerLogout();
    }, AUTO_LOGOUT_LIMIT);

    return () => {
      if (autoLogoutRef.current) {
        clearTimeout(autoLogoutRef.current);
      }
    };
  }, [showModal]);

  useEffect(() => {
    if (!logoutSuccess) return;

    authStore.reset();
    resetLogout();
    localStorage.removeItem(STORAGE_KEY);

    const userType = getUserTypeFromLocalStorage();

    switch (userType) {
      case userTypeEnum[userTypeEnum.SuperAdmin]:
        history.push(RoutesConstants.AdminLogin);
        break;
      case userTypeEnum[userTypeEnum.Customer]:
        history.push(RoutesConstants.CustomerLogin);
        break;
      case userTypeEnum[userTypeEnum.Staff]:
        history.push(RoutesConstants.StaffLogin);
        break;
      case userTypeEnum[userTypeEnum.Store]:
        history.push(RoutesConstants.StoreLogin);
        break;
      case userTypeEnum[userTypeEnum.Group]:
        history.push(RoutesConstants.GroupLogin);
        break;
      case userTypeEnum[userTypeEnum.HeadQuarter]:
        history.push(RoutesConstants.HeadQuarterLogin);
        break;
      default:
        history.push(RoutesConstants.PageNotFound);
    }

    setShowModal(false);
  }, [logoutSuccess]);

  useEffect(() => {
    if (!logoutError) return;
    toast.error(formatMessage(logoutError));
    resetLogout();
  }, [logoutError]);

  const handleContinue = () => {
    writeLastActivity();
    setShowModal(false);
    scheduleIdleCheck();
  };

  const handleManualLogout = () => {
    triggerLogout();
  };

  return (
    <Modal show={showModal} centered size="sm">
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="inactivity_title" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p><FormattedMessage id="inactivity_warning" /></p>
        <p className="mt-3">
          <FormattedMessage id="continue_confirm" />
        </p>
      </Modal.Body>
      <Modal.Footer>
          <Button
            variant="light-primary"
            type="button"
            className="btn btn-primary"
            onClick={handleContinue}
          >
          <FormattedMessage id="button_yes" />
        </Button>
        <Button 
        variant="danger"
            className="btn btn-danger"
            type="button"
            onClick={handleManualLogout}
            >
          <FormattedMessage id="button_no" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default observer(InactivityPopup);